import * as React from "react";

import { useNavigate, useParams } from "react-router-dom";

import { ChartRouteBaseParams } from "modules";
import { useActiveCharts } from "modules/charts-shared/api";
import { useUserRoles } from "contexts/UserRoles";

/**
 * Returns a callback that navigates back to a valid, selected chart after the
 * user cancels out of the Chart Summary flow.
 *
 * Chart selection in this app is URL-driven (the `:chartType/:chartCode`
 * segments). The summary Cancel buttons used to navigate to `../Chart` with no
 * chart code, which fell through to a hard-coded `C003x` provider chart and
 * produced a "Chart Application not found" error when that chart did not exist.
 *
 * Instead we resolve a real chart from the encounter's active chart
 * applications, preferring one that matches the user's role (nurse -> Triage,
 * provider -> Chart), then the first existing chart, and finally the
 * `general` route (Patient Info / Vitals) which needs no chart application.
 *
 * Exception: when the summary was opened from Medical Records (an
 * `encounter/:encounterId/...` route rather than a `charts/...` route), Cancel
 * returns to the Medical Records search view instead of a chart.
 */
function useReturnToSelectedChart() {
  const { encounterId = "" } = useParams<ChartRouteBaseParams>();
  const { data: activeCharts } = useActiveCharts(encounterId);
  const { roles } = useUserRoles();
  const navigate = useNavigate();

  return React.useCallback(() => {
    // The app uses HashRouter (contexts/ReactRouter.tsx), so the route always
    // lives in window.location.hash. Derive the current path + top-level route
    // (e.g. "in-process", "medical-records") from it, mirroring getChartRoute in
    // InProcessPatients.tsx.
    const path = window.location.hash.substring(1).split("?")[0];
    const mainRoute = path.split("/")[1] || "in-process";

    // Medical Records opens the summary via an `encounter/:encounterId/...`
    // route (not a `charts/...` route) and its Cancel UX is to return to the
    // encounter search view. Short-circuit before resolving a chart route —
    // otherwise we would silently land on the registered `/<mainRoute>/charts/…`
    // Charts view instead of search.
    if (path.includes("/encounter/")) {
      navigate(`/${mainRoute}/search`);
      return;
    }

    const charts = activeCharts?.data ?? [];
    const nurseCharts = charts.filter((chart) => chart.chartType === "Triage");
    // `Observation` charts are grouped with the provider charts and routed via
    // the `chart/...` path — mirroring ChartPicker, which merges the non-Triage
    // "other" bucket into provider (e.g. the C070a observation chart resolves to
    // a `Chart/<code>` route). Without this an encounter with only Observation
    // charts would fall back to `general/patient-info` despite a valid chart.
    const providerCharts = charts.filter(
      (chart) =>
        chart.chartType === "Chart" || chart.chartType === "Observation"
    );

    const isProvider =
      roles?.includes("Provider") || roles?.includes("SupervisingProvider");
    const isNurse = roles?.includes("Nurse");

    const nurseChartRoute = (chartCode: string) =>
      `/${mainRoute}/charts/${encounterId}/triage/${chartCode}/patient-info`;
    const providerChartRoute = (chartCode: string) =>
      `/${mainRoute}/charts/${encounterId}/chart/${chartCode}/patient-info`;

    // Preference: role match -> first existing -> Patient Info/Vitals (no error).
    // For a dual-role (Nurse + Provider) user, nurse is checked first — this
    // intentionally mirrors the default case of getChartRoute in
    // InProcessPatients.tsx, which also resolves the nursing chart before the
    // provider chart.
    let target: string;
    if (isNurse && nurseCharts.length > 0) {
      target = nurseChartRoute(nurseCharts[0].chartCode);
    } else if (isProvider && providerCharts.length > 0) {
      target = providerChartRoute(providerCharts[0].chartCode);
    } else if (nurseCharts.length > 0) {
      target = nurseChartRoute(nurseCharts[0].chartCode);
    } else if (providerCharts.length > 0) {
      target = providerChartRoute(providerCharts[0].chartCode);
    } else {
      target = `/${mainRoute}/charts/${encounterId}/general/patient-info`;
    }

    navigate(target);
  }, [activeCharts?.data, roles, encounterId, navigate]);
}

export { useReturnToSelectedChart };
